import {AllocationInput, SubmenuSettings, UpdateConnectionPrompt, UpdateIvrButton, UpdateIvrMenuMedia} from "@/api";
import {RecursiveOmit, SubstituteType} from "@/tools";
import {
    DomainExportAddressBookQuery,
    DomainExportQuery,
    ExtensionDevicesExportQuery,
    ExtensionProfileExportQuery,
    PhoneButtonsQuery,
} from "./queries.generated";

// These fields must be queried to get their enum variants, but we don't want to export them
export const keysToDelete = [
    "__typename",
    "numberId",
    "groupId",
    "quotaGroup",
    "quotaIvr",
    "quotaFax",
    "quotaCallQueue",
] as const;

export type KeysToOmit = (typeof keysToDelete)[number];

export type AudioFileWriter = (filename: string, data: Blob) => void;

export type ExtensionQuery = DomainExportQuery["users"]["items"][number];
export type ExtensionProfile = ExtensionProfileExportQuery["userProfiles"][number];
export type ClientDevice = ExtensionDevicesExportQuery["clientDevices"][number];
export type ThirdPartyDevice = ExtensionDevicesExportQuery["thirdPartyDevices"][number];
export type ProvisionedDevice = ExtensionDevicesExportQuery["provisionedDevices"][number];
export type PhoneButton = PhoneButtonsQuery["phoneButtons"][number];

export type RoutingQuery = DomainExportQuery["privateRouting"]["items"][number];

export type ClientDeviceExport = RecursiveOmit<ClientDevice, KeysToOmit>;
export type ThirdPartyDeviceExport = RecursiveOmit<ThirdPartyDevice, KeysToOmit>;
export type ProvisionedDeviceExport = RecursiveOmit<ProvisionedDevice & {buttons: PhoneButton[]}, KeysToOmit>;

export type IvrQuery = DomainExportQuery["ivrs"]["items"][number];
type IvrProfile = IvrQuery["profiles"][number];
export type IvrMediaExport = {
    description: string;
    disabled: boolean;
    mediaId: string;
};
// export type UpdateConnectionPromptExport = Omit<UpdateConnectionPrompt, "downloadToken" | "file" | "delete"> & {
//     mediaId?: string;
// };
// export type UpdateIvrMenuMediaExport = Omit<UpdateIvrMenuMedia, "downloadToken" | "file" | "id"> & {
//     mediaId?: string;
// };
export type UpdateIvrButtonExport = SubstituteType<UpdateIvrButton, UpdateConnectionPrompt, IvrMediaExport>;
export type SubmenuSettingsExport = SubstituteType<
    SubstituteType<SubmenuSettings, UpdateIvrButton, UpdateIvrButtonExport>,
    UpdateIvrMenuMedia,
    IvrMediaExport
>;
export type IvrProfileExport = Omit<IvrProfile, "actions" | "submenus" | "greetings"> & {
    buttons: UpdateIvrButtonExport[];
    submenus: SubmenuSettingsExport[];
    greetings: IvrMediaExport[];
    menuMedia: IvrMediaExport[];
};
export type IvrExport = RecursiveOmit<SubstituteType<IvrQuery, IvrProfile, IvrProfileExport>, KeysToOmit>;

export type LoginDataExport = {
    language: string;
    passwordHash: string;
    givenName?: string | null;
    familyName?: string | null;
    email?: string | null;
};

export type DomainAdminUserExport = {
    aliases: string[];
    user: LoginDataExport;
};

export type SpeedDialExport = {
    shortNumber: string;
    targetNumber: string;
    description: string;

    // Fields that are exported from old system (treated as address book entries)
    addressBookFamilyName?: string;
    addressBookGivenName?: string;
    addressBookCompany?: string;
};

export type ExtensionProfileExport = Omit<ExtensionProfile, KeysToOmit>;
export type ExtensionExport = RecursiveOmit<
    DomainExportQuery["users"]["items"][number] & {
        profiles: ExtensionProfileExport[];
        devices: {
            client: ClientDeviceExport[];
            thirdParty: ThirdPartyDeviceExport[];
            provisioned: ProvisionedDeviceExport[];
        };
        limits: {
            capabilitySets: string[];
            deviceTypeSets: AllocationInput[];
        };
        login?: LoginDataExport;
        speeddials: SpeedDialExport[];
    },
    KeysToOmit
>;
export type LocationExport = RecursiveOmit<DomainExportQuery["locations"]["items"][number], KeysToOmit>;
export type GroupExport = RecursiveOmit<
    DomainExportQuery["groups"]["items"][number] & {acwLength?: number},
    KeysToOmit
>;
export type FaxEmailsQuery = DomainExportQuery["faxesEmails"]["items"][number];
export type FaxExport = RecursiveOmit<
    DomainExportQuery["faxes"]["items"][number] & {
        additionalEmails: Omit<FaxEmailsQuery, "faxId">[];
    },
    KeysToOmit
>;
export type GatewayExport = RecursiveOmit<
    DomainExportQuery["gateways"]["items"][number] & {
        routings?: DomainExportQuery["privateRouting"]["items"];
    },
    KeysToOmit
>;

export type DomainExportSection = Omit<
    DomainExportQuery["domains"]["items"][number] & {
        allocations: {
            // NOTE: 'service endpoints' were renamed to 'services', but here we still need to use the old name
            // for compatibility
            serviceEndpoints: AllocationInput[];
            userPackages: AllocationInput[];
            capabilitySets: AllocationInput[];
            deviceTypeSets: AllocationInput[];
        };
    },
    KeysToOmit
>;

export type DomainExportAddressBook = RecursiveOmit<
    DomainExportAddressBookQuery["addressBook"]["entries"][number],
    KeysToOmit
>;

export type DomainExportYaml = Omit<
    {
        domain: DomainExportSection;
        users: ExtensionExport[];
        locations: LocationExport[];
        ivrs: IvrExport[];
        groups: GroupExport[];
        faxes: FaxExport[];
        gateways: GatewayExport[];
        addressBook: DomainExportAddressBook[];
        domainAdmins?: DomainAdminUserExport[];
    },
    KeysToOmit
>;
